import moment from "jalali-moment";

/**
 * Multi-alarm helpers for "Add event".
 *
 * Model (stored in redux `events.fields.alarms`) – an array of:
 *   { kind: "minutes", value: 0 | 60 | 120 | 180 }  -> same day, N minutes before the event
 *   { kind: "clock",   value: "HH:mm" }             -> same day, a custom clock
 *   { kind: "days",    value: 1 | 2 }               -> N days before, SAME clock as the event
 *
 * Server side every alarm becomes its own event (the API only accepts one
 * `alarm_at` per event), so everything here ends up as a list of
 * "YYYY:MM:DD HH:mm:ss" UTC strings – the exact format the old code sent.
 */

// Iran has no DST since 2022, so a fixed +03:30 is correct.
export const IRAN_UTC_OFFSET_MIN = 210;

// Existing behaviour: same-day presets fire 1 minute before the computed time.
export const ALARM_PRESET_LEAD_MIN = 1;

export const MAX_ALARMS = 6;

// Fallback; the real price text comes from base-data on the server.
export const SMS_COST_TOMAN = 400;

export const SAME_DAY_PRESETS = [
  { kind: "minutes", value: 0, text: "در لحظه" },
  { kind: "minutes", value: 60, text: "یک ساعت قبل" },
  { kind: "minutes", value: 120, text: "دو ساعت قبل" },
  { kind: "minutes", value: 180, text: "سه ساعت قبل" },
];

export const DAYS_BEFORE_OPTIONS = [
  { kind: "days", value: 1, text: "یک روز قبل" },
  { kind: "days", value: 2, text: "دو روز قبل" },
];

export const SERVER_FORMAT = "YYYY:MM:DD HH:mm:ss";

const FA_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export const toFaDigits = (value) =>
  String(value).replace(/\d/g, (d) => FA_DIGITS[d]);

export const alarmKey = (alarm) => `${alarm.kind}:${alarm.value}`;

/**
 * The clock picker hands back raw slide text (e.g. " 09 " with spaces), so
 * accept anything like "9 : 5" / " 09 :30" / Persian digits and return a
 * clean "HH:mm" – or null when it isn't a valid time.
 */
export const normalizeClock = (clock) => {
  const latin = String(clock ?? "")
    .replace(/[۰-۹]/g, (d) => String(FA_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));

  const match = latin.match(/(\d{1,2})\s*:\s*(\d{1,2})/);

  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);

  if (hour > 23 || minute > 59) return null;

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

/**
 * Event wall-clock time (Tehran) as a UTC moment, so all arithmetic is free
 * of browser time-zone / DST surprises.
 * englishDate: "YYYY-MM-DD" or "YYYY/MM/DD", clock: "HH:mm"
 */
export const eventWallMoment = (englishDate, clock) => {
  const normalized = normalizeClock(clock);

  if (!englishDate || !normalized) return null;

  const m = moment.utc(
    `${englishDate.split("/").join("-")} ${normalized}`,
    "YYYY-MM-DD HH:mm",
    true,
  );

  return m.isValid() ? m : null;
};

/** Wall-clock (Tehran) -> string the server expects (UTC). */
export const toServerString = (wallMoment) =>
  wallMoment
    .clone()
    .subtract(IRAN_UTC_OFFSET_MIN, "minutes")
    .format(SERVER_FORMAT);

/** Wall-clock moment of an alarm (what the user sees on a Tehran clock). */
export const alarmWallMoment = (alarm, eventWall) => {
  if (alarm.kind === "minutes")
    return eventWall.clone().subtract(alarm.value, "minutes");

  if (alarm.kind === "days")
    return eventWall.clone().subtract(alarm.value, "days");

  const [hour, minute] = String(alarm.value).split(":").map(Number);

  return eventWall.clone().set({ hour, minute, second: 0, millisecond: 0 });
};

const leadMinutes = (alarm) =>
  alarm.kind === "minutes" ? ALARM_PRESET_LEAD_MIN : 0;

export const alarmToServerString = (alarm, eventWall) =>
  toServerString(
    alarmWallMoment(alarm, eventWall).subtract(leadMinutes(alarm), "minutes"),
  );

/**
 * "ok" | "past" (already gone) | "after" (not before the event).
 * nowMs = real current instant (Date.now() + server difference).
 */
export const getAlarmStatus = (alarm, eventWall, nowMs) => {
  // the moment the alarm really fires (presets include the 1 minute lead,
  // so "در لحظه" is 1 minute before the event and counts as "before")
  const fireWall = alarmWallMoment(alarm, eventWall).subtract(
    leadMinutes(alarm),
    "minutes",
  );

  if (!fireWall.isBefore(eventWall)) return "after";

  const fireInstant = fireWall.valueOf() - IRAN_UTC_OFFSET_MIN * 60000;

  // small buffer so an alarm can't be "in the past" by the time the request lands
  if (fireInstant <= nowMs + 30000) return "past";

  return "ok";
};

/** Same moment on the clock? (used to reject "11:00" when "1h before 12:00" exists) */
export const isDuplicateAlarm = (alarms, candidate, eventWall) => {
  const target = alarmWallMoment(candidate, eventWall).valueOf();

  return alarms.some(
    (item) => alarmWallMoment(item, eventWall).valueOf() === target,
  );
};

/**
 * Turn the user's selection into what we actually send:
 * valid only, de-duplicated by server string, ordered earliest first.
 * Returns [{ alarm, serverString }]
 */
export const resolveAlarms = (alarms, eventWall, nowMs) => {
  if (!eventWall || !Array.isArray(alarms)) return [];

  const seen = new Set();
  const result = [];

  alarms.forEach((alarm) => {
    if (getAlarmStatus(alarm, eventWall, nowMs) !== "ok") return;

    const serverString = alarmToServerString(alarm, eventWall);

    if (seen.has(serverString)) return;

    seen.add(serverString);
    result.push({ alarm, serverString });
  });

  return result.sort((a, b) => (a.serverString < b.serverString ? -1 : 1));
};

/** "۱۱:۰۰" */
export const formatClockFa = (wallMoment) => toFaDigits(wallMoment.format("HH:mm"));

/** "دوشنبه ۲۰ مهر" */
export const formatDayFa = (wallMoment) =>
  toFaDigits(wallMoment.clone().locale("fa").format("dddd jD jMMMM"));

export const alarmText = (alarm) => {
  if (alarm.kind === "clock") return "ساعت دلخواه";

  const list =
    alarm.kind === "days" ? DAYS_BEFORE_OPTIONS : SAME_DAY_PRESETS;

  return list.find((item) => item.value === alarm.value)?.text || "";
};

export const formatToman = (value) =>
  `${toFaDigits(value.toLocaleString("en")).replace(/,/g, "٬")} تومان`;
